import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Trash2, X } from 'lucide-react';

import { SPAN_TYPES, TAG_META, fieldFor, operationFor } from '../../lib/spans.js';
import { Keycap } from '../common/index.jsx';
import { SpanOutcome } from './SpanOutcome.jsx';

/**
 * Edit a tag in place, right where it sits in the caption.
 *
 * This is the piece that made the old tool quick: the moment you tag a span the popover
 * opens with the relevant field focused, so a hallucination replacement is
 * select → `h` → type → Enter, without hunting for the span in a list further down the
 * page.
 */
export function SpanPopover({ span, index, anchor, onUpdate, onRetag, onRemove, onClose }) {
  const ref = useRef(null);
  const firstFieldRef = useRef(null);
  const [position, setPosition] = useState({ top: -9999, left: -9999 });

  // The action is derived, never chosen: a hallucination removes unless a replacement
  // is typed, wrong-uncertainty always removes, uncertain always keeps with a note.
  const field = fieldFor(span?.label);
  const operation = operationFor(span?.label, span || {});

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node || !anchor) return;
    const width = node.offsetWidth;
    const height = node.offsetHeight;
    const gap = 8;

    let top = anchor.bottom + gap;
    if (top + height > window.innerHeight - gap) top = Math.max(gap, anchor.top - height - gap);
    let left = anchor.left + anchor.width / 2 - width / 2;
    left = Math.max(gap, Math.min(left, window.innerWidth - width - gap));
    setPosition({ top, left });
  }, [anchor, field?.key]);

  // Put the caret straight into the field this tag actually needs.
  useEffect(() => {
    firstFieldRef.current?.focus();
    firstFieldRef.current?.select?.();
  }, [index, field?.key]);

  useEffect(() => {
    const onAway = (e) => {
      if (ref.current && !ref.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', onAway);
    return () => document.removeEventListener('mousedown', onAway);
  }, [onClose]);

  if (!span) return null;

  const meta = TAG_META[span.label] || {};

  return (
    <div
      ref={ref}
      className="span-popover"
      style={{ top: position.top, left: position.left }}
      role="dialog"
      aria-label={`Edit the ${meta.label} tag`}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.stopPropagation();
          onClose();
        }
        // Enter commits and closes, so the fast path never needs the mouse.
        if (e.key === 'Enter' && e.target.tagName !== 'TEXTAREA') {
          e.preventDefault();
          e.stopPropagation();
          onClose();
        }
      }}
    >
      <div className="span-popover-head">
        <span className={`chip ${span.label}`}>{meta.label}</span>
        <span className="span-popover-quote" title={span.text}>
          “{span.text}”
        </span>
        <button type="button" className="btn ghost icon" onClick={onClose} aria-label="Close">
          <X size={14} />
        </button>
      </div>

      <div className="span-popover-row">
        {SPAN_TYPES.map((label) => (
          <button
            key={label}
            type="button"
            className={`tag-btn ${label} ${label === span.label ? 'current' : ''}`}
            title={TAG_META[label].help}
            onClick={() => onRetag(index, label)}
          >
            {TAG_META[label].label}
          </button>
        ))}
      </div>

      {field && (
        <label className="span-popover-field">
          <span>
            {field.label}
            {field.required && <em className="required"> required</em>}
          </span>
          <input
            ref={firstFieldRef}
            className="input sm"
            value={span[field.key] || ''}
            placeholder={field.placeholder}
            onChange={(e) => onUpdate(index, { [field.key]: e.target.value })}
          />
        </label>
      )}

      <SpanOutcome span={span} />

      <div className="span-popover-foot">
        <button type="button" className="btn sm danger" onClick={() => onRemove(index)}>
          <Trash2 size={12} /> Remove tag
        </button>
        <span className="grow" />
        <span className="tiny faint">
          <Keycap>⏎</Keycap> done · <Keycap>esc</Keycap> close
        </span>
      </div>
    </div>
  );
}
